/**
 * Получить заголовки с авторизацией
 */
import { invalidateStoredSession } from "./sessionEvents";

const getHeaders = (includeAuth = true, contentType = "application/json") => {
    const headers = {};

    if (contentType) {
        headers["Content-Type"] = contentType;
    }

    if (includeAuth) {
        const token = localStorage.getItem("jwtToken");
        if (token) {
            headers["Authorization"] = `Bearer ${token}`;
        }
    }

    return headers;
};

const appendUserFieldsToFormData = (formData) => {
    const userId = localStorage.getItem("user-id");
    const userName = localStorage.getItem("user-login");
    const userPassword = localStorage.getItem("user-password");

    if (userId) {
        formData.append("user_id", userId);
    }
    if (userName) {
        formData.append("user_name", userName);
    }
    if (userPassword) {
        formData.append("user_password", userPassword);
    }

    return formData;
};

/**
 * Получить стандартное тело запроса с пользовательскими данными
 */
const getUserBody = (additionalData = {}) => {
    const userId = localStorage.getItem("user-id");
    const userName = localStorage.getItem("user-login");
    const userPassword = localStorage.getItem("user-password");

    return JSON.stringify({
        user_id: userId,
        user_name: userName,
        user_password: userPassword,
        ...additionalData,
    });
};

/**
 * Обработка ответа
 */
const createApiError = (message, status) => {
    const error = new Error(message);
    error.status = status;
    return error;
};

export const isUnauthorizedError = (error) =>
    error?.status === 401 || error?.status === 403;

const handleResponse = async (
    response,
    { invalidateSession = true } = {},
) => {
    if (!response.ok) {
        if (
            invalidateSession &&
            (response.status === 401 || response.status === 403)
        ) {
            invalidateStoredSession();
        }
        const error = await response.json().catch(() => ({}));
        throw createApiError(
            error.message || `HTTP error! Status: ${response.status}`,
            response.status,
        );
    }

    // Проверка на пустой ответ
    const contentLength = response.headers.get("Content-Length");
    if (contentLength === "0") {
        return null;
    }

    return response.json();
};

/**
 * API Client methods
 */
export const apiClient = {
    /**
     * GET запрос
     */
    get: async (endpoint, params = {}, includeAuth = true) => {
        // Если endpoint уже полный URL, используем его напрямую
        const url = new URL(endpoint);

        // Добавляем параметры
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                url.searchParams.append(key, value);
            }
        });

        const response = await fetch(url.toString(), {
            method: "GET",
            headers: getHeaders(includeAuth),
        });

        return handleResponse(response);
    },

    /**
     * POST запрос
     */
    post: async (
        endpoint,
        data = {},
        includeAuth = true,
        useUserData = true,
    ) => {
        const body = useUserData ? getUserBody(data) : JSON.stringify(data);

        const response = await fetch(endpoint, {
            method: "POST",
            headers: getHeaders(includeAuth),
            body,
        });

        return handleResponse(response);
    },

    /**
     * PUT запрос
     */
    put: async (
        endpoint,
        data = {},
        includeAuth = true,
        useUserData = false,
    ) => {
        const body = useUserData ? getUserBody(data) : JSON.stringify(data);

        const response = await fetch(endpoint, {
            method: "PUT",
            headers: getHeaders(includeAuth),
            body,
        });

        return handleResponse(response);
    },

    /**
     * DELETE запрос
     */
    delete: async (endpoint, data = {}, includeAuth = true) => {
        const body = getUserBody(data);

        const response = await fetch(endpoint, {
            method: "DELETE",
            headers: getHeaders(includeAuth),
            body,
        });

        return handleResponse(response);
    },

    // Специальный метод для регистрации (без авторизации)
    postPublic: async (endpoint, data) => {
        const response = await fetch(endpoint, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(data),
        });

        return handleResponse(response, { invalidateSession: false });
    },

    postForm: async (endpoint, formData, includeAuth = true, useUserData = true) => {
        const body = useUserData
            ? appendUserFieldsToFormData(formData)
            : formData;

        const response = await fetch(endpoint, {
            method: "POST",
            headers: getHeaders(includeAuth, null),
            body,
        });

        return handleResponse(response);
    },

    putForm: async (endpoint, formData, includeAuth = true, useUserData = true) => {
        const body = useUserData
            ? appendUserFieldsToFormData(formData)
            : formData;

        const response = await fetch(endpoint, {
            method: "PUT",
            headers: getHeaders(includeAuth, null),
            body,
        });

        return handleResponse(response);
    },
};

export default apiClient;
